import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, Sparkles } from 'lucide-react';
import { FlexPlanEditor } from './FlexPlanEditor';
import { saveFlexPlan, listFlexPlans } from '../services/flexPlans';
import {
  nextEditableMonth,
  isPlanLocked,
  getFlexConfig,
  splitFlexPricing,
  editablePlanMonths,
  suggestPlanDates,
  addMonths,
  monthStartOf,
  formatPlanDayNumbers,
  type FlexPlanMonthLike,
} from '../utils/flexPlan';
import { useFlexPricingRows } from '../hooks/useFlexPricingRows';
import { blackoutDateSet, isBlackoutDate } from '../config/blackoutDates';
import { DEFAULT_BRAND_KEY, type BrandKey } from '../config/brands';
import { useLabels, useMonthLabel, useFlexStatusLabel } from '../config/useLabels';
import { type FlexMonthPlan } from '../types/program';
import { formatDateDisplay, getTodayDateStringInBusinessTZ } from '../utils/date';

interface Props {
  enrollmentId: string;
  programId: string;
  /**
   * Which brand's closure calendar applies. The server is already brand-aware
   * (`flex_plan_save` filters by it), so this keeps the client's own pre-save filter and the
   * calendar it draws from disagreeing with the row that actually gets written.
   */
  brandKey?: BrandKey;
  scheduleCron?: string;
  /** Program end date (YYYY-MM-DD) — caps how far the plan-ahead months extend. */
  programEndDate?: string | null;
  /** Enrollment start date (YYYY-MM-DD) — no month before this is editable, and days
   *  before it within the start month are not selectable. */
  enrollmentStartDate?: string | null;
  /** `line_item_configs` ids this family declined. Without them the quoted per-day rate
   *  includes optional extras they never bought, and the estimate overstates the bill. */
  /** The optional line_item_config ids this enrollment pays for. */
  optInItems?: string[] | null;
}

/**
 * Parent-facing flex plan surface for one enrollment. It edits the next editable (coming)
 * month plus up to `flex.planAheadMonths` future months for planning convenience — each
 * gated on the prior month already being saved (UI-only), clearly badged as a not-yet-billed
 * "plan-ahead draft", and prefilled from the family's usual cadence. Billing is on the
 * confirmed plan, not attendance.
 */
export function FlexPlanManager({ enrollmentId, programId, brandKey = DEFAULT_BRAND_KEY, scheduleCron, programEndDate, enrollmentStartDate, optInItems = null }: Props) {
  const queryClient = useQueryClient();
  const t = useLabels();
  const monthLabelFn = useMonthLabel();
  const flexStatusLabel = useFlexStatusLabel();
  /** Bilingual month label from a YYYY-MM string. */
  const monthLabel = (ym: string, short = false) => {
    const [yy, mm] = ym.split('-').map(Number);
    return short ? monthLabelFn(null, mm - 1, { short: true }) : monthLabelFn(yy, mm - 1);
  };
  const lockDay = getFlexConfig().lockDay;

  const months = useMemo(
    () => editablePlanMonths(undefined, undefined, programEndDate ?? null, enrollmentStartDate ?? null),
    [programEndDate, enrollmentStartDate],
  );
  // First editable month for this enrollment (its start month, or the rolling coming month).
  const baseMonth = months[0] ?? nextEditableMonth();

  const [selectedMonth, setSelectedMonth] = useState(baseMonth);

  const { data: flexPricing } = useFlexPricingRows(programId, scheduleCron);
  const pricing = flexPricing
    ? (() => {
        const { perDayRate, monthlyAddOns } = splitFlexPricing(flexPricing.rows, { optIn: optInItems });
        return { perDay: perDayRate, addOns: monthlyAddOns, weekdays: flexPricing.weekdays };
      })()
    : undefined;

  const { data: history } = useQuery<FlexMonthPlan[]>({
    queryKey: ['flex-plans', enrollmentId],
    enabled: !!enrollmentId,
    queryFn: () => listFlexPlans(enrollmentId),
  });

  const plansByMonth = useMemo(() => {
    const map = new Map<string, FlexMonthPlan>();
    for (const p of history ?? []) map.set(p.plan_month.slice(0, 7), p);
    return map;
  }, [history]);
  const hasRow = (mo: string) => plansByMonth.has(mo.slice(0, 7));

  // Months that are locked but not yet history get read-only tabs ahead of the editable ones,
  // whenever a plan exists. That is always the month the child is attending *right now* —
  // parents look for it first, "where is August?" — and, from the day after the lock until
  // the month turns, next month too: it has frozen, so `months` has moved on to the one after,
  // and without a tab of its own the plan the family is about to be billed for vanishes.
  const currentMonth = monthStartOf(getTodayDateStringInBusinessTZ());
  const lockedMonths: string[] = [];
  for (let mo = currentMonth; mo < (months[0] ?? baseMonth); mo = addMonths(mo, 1)) {
    if (hasRow(mo)) lockedMonths.push(mo);
  }
  const viewMonths = [...lockedMonths, ...months];
  const activeMonth = viewMonths.includes(selectedMonth) ? selectedMonth : baseMonth;
  const isLockedView = lockedMonths.includes(activeMonth);
  const isCurrentMonth = isLockedView && activeMonth === currentMonth;

  const [draftByMonth, setDraftByMonth] = useState<Record<string, string[]>>({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const isComing = activeMonth === baseMonth;

  // Suggested prefill for a plan-ahead month with no saved plan yet: infer the family's
  // usual cadence from recent confirmed months, else the program schedule, minus holidays.
  const suggestion = useMemo(() => {
    if (isComing || hasRow(activeMonth)) return null;
    const sourceMonths: FlexPlanMonthLike[] = (history ?? [])
      .filter((p) => p.plan_month.slice(0, 7) < activeMonth.slice(0, 7) && (p.dates?.length ?? 0) > 0)
      .sort((a, b) => (a.plan_month < b.plan_month ? 1 : -1))
      .slice(0, 3)
      .map((p) => ({ planMonth: p.plan_month, dates: p.dates ?? [] }));
    return suggestPlanDates(activeMonth, {
      sourceMonths,
      fallbackWeekdays: pricing?.weekdays ?? [],
      holidays: blackoutDateSet(brandKey),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComing, activeMonth, history, pricing?.weekdays, plansByMonth]);

  const plan = plansByMonth.get(activeMonth.slice(0, 7)) ?? null;
  const current = draftByMonth[activeMonth] ?? plan?.dates ?? suggestion?.dates ?? [];
  const locked = isPlanLocked(activeMonth);
  const lockDateYmd = `${addMonths(activeMonth, -1).slice(0, 7)}-${String(lockDay).padStart(2, '0')}`;
  const showPrefillNote =
    !!suggestion && suggestion.dates.length > 0 && draftByMonth[activeMonth] === undefined;

  async function save() {
    setSaving(true);
    setMsg(null);
    try {
      // Enforce blackout days server-side too: never persist a closed/camp day.
      await saveFlexPlan(enrollmentId, activeMonth, current.filter((d) => !isBlackoutDate(d, brandKey)), {
        previousDates: plan?.dates ?? null,
        actorType: 'parent',
      });
      setDraftByMonth((prev) => {
        const next = { ...prev };
        delete next[activeMonth];
        return next;
      });
      await queryClient.invalidateQueries({ queryKey: ['flex-plans', enrollmentId] });
      setMsg('Saved — thank you!');
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Could not save your plan');
    } finally {
      setSaving(false);
    }
  }

  // Everything older than the first month with its own tab — the current month moved up into
  // the tab row, so it must not also appear here.
  const firstTabMonth = viewMonths[0] ?? baseMonth;
  const priorMonths = (history ?? []).filter((p) => p.plan_month.slice(0, 7) < firstTabMonth.slice(0, 7));

  return (
    <div className="mt-4">
      <h3 className="text-sm font-medium text-gray-500 mb-2">
        {t('flex.planTitle')}{viewMonths.length > 1 ? '' : ` — ${monthLabel(baseMonth.slice(0, 7))}`}
      </h3>

      {(
        <Link
          to={`/assistant?source=flex_plan&q=${encodeURIComponent(
            'How does my Flex Monthly plan work — when it locks, planning future months, and why some days are blocked?',
          )}`}
          className="inline-flex items-center gap-1 mb-2 text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"
        >
          <Sparkles className="h-3.5 w-3.5" />
          {t('flex.askAssistant')}
        </Link>
      )}

      {viewMonths.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {viewMonths.map((mo) => {
            // Index within the *editable* months; -1 for a read-only locked month.
            const i = months.indexOf(mo);
            const isReadOnly = i === -1;
            const isCurrent = isReadOnly && mo === currentMonth;
            // A plan-ahead month opens only once the month before it is saved.
            const enabled = isReadOnly || i === 0 || hasRow(months[i - 1]);
            const isActive = mo === activeMonth;
            return (
              <button
                key={mo}
                type="button"
                disabled={!enabled}
                onClick={() => {
                  setSelectedMonth(mo);
                  setMsg(null);
                }}
                title={!enabled ? `Save ${monthLabel(months[i - 1].slice(0, 7))} first` : undefined}
                aria-current={isActive ? 'true' : undefined}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                  isActive
                    ? 'bg-blue-600 text-white border-blue-600'
                    : enabled
                    ? 'bg-white text-gray-700 border-gray-200 hover:border-blue-300'
                    : 'bg-gray-50 text-gray-400 border-gray-100 cursor-not-allowed'
                }`}
              >
                {monthLabel(mo.slice(0, 7), true)}
                {isCurrent && (
                  <span className={isActive ? 'text-blue-100' : 'text-gray-500'}>· {t('flex.thisMonth')}</span>
                )}
                {isReadOnly && !isCurrent && (
                  <span className={isActive ? 'text-blue-100' : 'text-gray-500'}>· {t('flex.nextMonth')}</span>
                )}
                {i > 0 && <span className={isActive ? 'text-blue-100' : 'text-gray-400'}>· ahead</span>}
                {hasRow(mo) && (
                  <span className={`h-1.5 w-1.5 rounded-full ${isActive ? 'bg-white' : 'bg-emerald-500'}`} />
                )}
                {i === 0 && (
                  <span className={`font-semibold ${isActive ? 'text-amber-200' : 'text-amber-600'}`}>
                    locks {Number(addMonths(mo, -1).slice(5, 7))}/{lockDay}
                  </span>
                )}
                {(!enabled || isReadOnly) && <Lock className="h-3 w-3" />}
              </button>
            );
          })}
        </div>
      )}

      {isLockedView ? (
        <div className="mb-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
          <p className="text-xs text-gray-600">{t(isCurrentMonth ? 'flex.thisMonthNote' : 'flex.nextMonthNote')}</p>
        </div>
      ) : isComing ? (
        <p className="text-xs text-gray-500 mb-2">
          Pick the days {enrollmentId ? 'your child' : 'you'} will attend next month. You&apos;re billed for the
          days confirmed here × the per-day rate — <strong>not</strong> attendance, so remove any days you know
          you&apos;ll miss before the {lockDay}th. After the {lockDay}th the plan locks for the month.
        </p>
      ) : (
        <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
          <span className="inline-block text-[11px] font-semibold uppercase tracking-wide text-amber-700">
            {t('flex.planningAhead')}
          </span>
          <p className="text-xs text-amber-800 mt-0.5">
            This just lets you plan {monthLabel(activeMonth.slice(0, 7))} early — it does <strong>not</strong> change
            your {monthLabel(baseMonth.slice(0, 7))} bill. It locks on {formatDateDisplay(lockDateYmd)}; you can edit
            and re-save any time before then.
          </p>
        </div>
      )}

      {showPrefillNote && (
        <p className="text-xs text-blue-700 mb-2">
          Prefilled from {suggestion?.source === 'history' ? 'your usual days' : 'the program schedule'}
          {suggestion && suggestion.removedHolidays.length > 0
            ? ` — ${suggestion.removedHolidays.length} closed day${suggestion.removedHolidays.length > 1 ? 's' : ''} skipped`
            : ''}
          . Adjust and save to confirm.
        </p>
      )}

      {locked ? (
        <FlexPlanEditor planMonth={activeMonth} perDayRate={pricing?.perDay ?? 0} dates={current} monthlyAddOns={pricing?.addOns ?? 0} scheduleWeekdays={pricing?.weekdays ?? []} enrollmentStartYmd={enrollmentStartDate} brandKey={brandKey} readOnly />
      ) : (
        <>
          <FlexPlanEditor
            planMonth={activeMonth}
            perDayRate={pricing?.perDay ?? 0}
            dates={current}
            onChange={(dates) => setDraftByMonth((prev) => ({ ...prev, [activeMonth]: dates }))}
            monthlyAddOns={pricing?.addOns ?? 0}
            scheduleWeekdays={pricing?.weekdays ?? []}
            enrollmentStartYmd={enrollmentStartDate}
            brandKey={brandKey}
          />
          <div className="flex items-center gap-3 mt-2">
            <button
              type="button"
              onClick={() => void save()}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : t('action.savePlan')}
            </button>
            {!isComing && (
              <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                {t('flex.planAheadDraft')}
              </span>
            )}
            {msg && <span className="text-xs text-gray-600">{msg}</span>}
          </div>
        </>
      )}

      {priorMonths.length > 0 && (
        <div className="mt-4">
          <h4 className="text-xs font-medium text-gray-500 mb-1">{t('flex.pastMonths')}</h4>
          <ul className="text-xs text-gray-600 space-y-1">
            {priorMonths.map((p) => {
              // "7 days" doesn't answer the question a parent actually asks about a past
              // month — *which* days. The month is named on the line above, so the dates
              // are listed as bare day numbers.
              const dayNumbers = formatPlanDayNumbers(p.dates, p.plan_month);
              return (
                <li key={p.id} className="space-y-0.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-800">{monthLabel(p.plan_month.slice(0, 7))}</span>
                    <span>
                      {p.status === 'skipped' ? 'paused (no charge)' : `${p.dates?.length ?? 0} day${(p.dates?.length ?? 0) !== 1 ? 's' : ''}`}
                    </span>
                    <span className="text-gray-400">· {flexStatusLabel(p.status)}</span>
                    {p.locked_at && <span className="text-gray-400">· {formatDateDisplay(p.locked_at.slice(0, 10))}</span>}
                  </div>
                  {dayNumbers && (
                    <div className="text-gray-500">
                      {t('flex.daysAttended')}: {dayNumbers}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

export default FlexPlanManager;
